/* © NXAPP Limited */
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import {
  Users, Mail, Send, Building2, Upload, Layers, Megaphone, Eye, Clock,
} from 'lucide-react'
import { formatDate, formatDateTime } from '@/lib/utils'
import StatusBadge from '@/components/ui/StatusBadge'
import Spinner from '@/components/ui/Spinner'
import { Link } from 'react-router-dom'
import type { IssueStatus } from '@/types/database'

function StatCard({ label, value, icon: Icon, color, to }: {
  label: string; value: number | string; icon: typeof Users; color: string; to: string
}) {
  return (
    <Link to={to} className="card p-4 flex items-center gap-3 hover:border-brand-300 hover:shadow-md transition-all">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xl font-bold text-gray-900 leading-tight">{value}</p>
        <p className="text-xs text-gray-500 truncate">{label}</p>
      </div>
    </Link>
  )
}

interface DashIssue {
  id: string
  subject: string
  status: IssueStatus
  created_at: string
  scheduled_at: string | null
  sent_at: string | null
  campaign_id: string | null
  campaigns: { name: string } | null
}

const ONGOING: IssueStatus[] = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'SCHEDULED', 'SENDING']

export default function DashboardPage() {
  const { user } = useAuthContext()

  // ── Module stats (one lightweight count each) ──
  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const uid = user!.id
      const count = async (q: PromiseLike<{ count: number | null }>) => (await q).count ?? 0
      const [subscribed, chCompanies, deliverable, imported, segments, campaigns, issuesSent, emailsSent, opens] = await Promise.all([
        count(supabase.from('contacts').select('id', { count: 'exact', head: true }).eq('user_id', uid).eq('status', 'SUBSCRIBED')),
        count(supabase.from('ch_companies').select('id', { count: 'exact', head: true })),
        count(supabase.from('officer_enrichment').select('id', { count: 'exact', head: true }).eq('domain_verified', true)),
        count(supabase.from('imported_leads').select('id', { count: 'exact', head: true }).eq('user_id', uid)),
        count(supabase.from('segments').select('id', { count: 'exact', head: true }).eq('user_id', uid)),
        count(supabase.from('campaigns').select('id', { count: 'exact', head: true }).eq('user_id', uid)),
        count(supabase.from('issues').select('id', { count: 'exact', head: true }).eq('user_id', uid).eq('status', 'SENT')),
        count(supabase.from('sends').select('id', { count: 'exact', head: true }).eq('user_id', uid).eq('status', 'SENT')),
        count(supabase.from('events').select('id', { count: 'exact', head: true }).eq('type', 'OPEN')),
      ])
      return { subscribed, chCompanies, deliverable, imported, segments, campaigns, issuesSent, emailsSent, opens }
    },
  })

  // ── This week + ongoing issues ──
  const { data: issues, isLoading } = useQuery({
    queryKey: ['dashboard-issues', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('issues')
        .select('id, subject, status, created_at, scheduled_at, sent_at, campaign_id, campaigns(name)')
        .eq('user_id', user!.id)
        // issues.campaign_id is ON DELETE SET NULL, so deleting a campaign leaves
        // its issues orphaned. They are not "current" and must not show here.
        .not('campaign_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(60)
      // Belt and braces: if the campaign row is gone the join comes back null.
      return ((data ?? []) as unknown as DashIssue[]).filter(i => !!i.campaigns)
    },
  })

  // ── Per-campaign breakdown — members, issues, emails sent ──
  const { data: campaignStats } = useQuery({
    queryKey: ['dashboard-campaign-stats', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const uid = user!.id
      const { data: camps } = await supabase
        .from('campaigns')
        .select('id, name, icon, colour, kind, audience')
        .eq('user_id', uid)
        .order('name')

      const [{ data: members }, { data: issueRows }, { data: sendRows }, { data: enrols }, { data: stepRows }] = await Promise.all([
        supabase.from('lead_campaign_assignments').select('campaign_id, status, created_at').eq('user_id', uid),
        supabase.from('issues').select('id, campaign_id, status').eq('user_id', uid).not('campaign_id', 'is', null),
        // enrollment_id matters as much as issue_id: sequence step sends have no
        // issue, so counting only via issues reports 0 for a sequence campaign
        // that has actually sent.
        supabase.from('sends').select('id, issue_id, enrollment_id, status').eq('user_id', uid),
        supabase.from('sequence_enrollments').select('id, campaign_id, status, last_step_no, enrolled_at').eq('user_id', uid),
        supabase.from('sequence_steps').select('id, campaign_id, step_no, subject').eq('user_id', uid).order('step_no'),
      ])

      // Rolling windows — "new this week/month" answers the daily question of
      // whether the top of the funnel is still filling.
      const wkStart = new Date(); wkStart.setDate(wkStart.getDate() - ((wkStart.getDay() + 6) % 7)); wkStart.setHours(0,0,0,0)
      const moStart = new Date(); moStart.setDate(1); moStart.setHours(0,0,0,0)
      const since = (iso: unknown, from: Date) => !!iso && new Date(iso as string) >= from

      const issueToCampaign = new Map<string, string>()
      for (const i of issueRows ?? []) issueToCampaign.set(i.id as string, i.campaign_id as string)
      const enrolToCampaign = new Map<string, string>()
      for (const e of enrols ?? []) enrolToCampaign.set(e.id as string, e.campaign_id as string)
      const sendCampaign = (s: { issue_id?: unknown; enrollment_id?: unknown }) =>
        issueToCampaign.get(s.issue_id as string) ?? enrolToCampaign.get(s.enrollment_id as string)

      return (camps ?? []).map(c => {
        const mine = (members ?? []).filter(m => m.campaign_id === c.id)
        const myIssues = (issueRows ?? []).filter(i => i.campaign_id === c.id)
        const mySends = (sendRows ?? []).filter(s => sendCampaign(s) === c.id)
        const myEnrols = (enrols ?? []).filter(e => e.campaign_id === c.id)
        const mySteps = (stepRows ?? []).filter(st => st.campaign_id === c.id)
        return {
          ...c,
          members: mine.length,
          activeMembers: mine.filter(m => m.status === 'active').length,
          membersThisWeek: mine.filter(m => since(m.created_at, wkStart)).length,
          membersThisMonth: mine.filter(m => since(m.created_at, moStart)).length,
          issues: myIssues.length,
          issuesSent: myIssues.filter(i => i.status === 'SENT').length,
          emailsSent: mySends.filter(s => s.status === 'SENT' || s.status === 'DELIVERED').length,
          enrolTotal: myEnrols.length,
          enrolActive: myEnrols.filter(e => e.status === 'active').length,
          enrolHold: myEnrols.filter(e => e.status === 'on_hold').length,
          enrolDone: myEnrols.filter(e => e.status === 'completed').length,
          enrolThisWeek: myEnrols.filter(e => since(e.enrolled_at, wkStart)).length,
          enrolThisMonth: myEnrols.filter(e => since(e.enrolled_at, moStart)).length,
          // Per step: how many have received it, and how many are still to.
          steps: mySteps.map(st => {
            const n = st.step_no as number
            const done = myEnrols.filter(e => (e.last_step_no as number) >= n).length
            const pending = myEnrols.filter(e => e.status === 'active' && (e.last_step_no as number) < n).length
            return { id: st.id as string, no: n, subject: (st.subject as string) || `Step ${n}`, done, pending }
          }),
        }
      })
    },
  })

  const now = new Date()
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7)); weekStart.setHours(0, 0, 0, 0)
  const weekEnd = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 7)

  const inThisWeek = (d: string | null) => {
    if (!d) return false
    const t = new Date(d)
    return t >= weekStart && t < weekEnd
  }
  const shown = (issues ?? [])
    .filter(i => ONGOING.includes(i.status) || inThisWeek(i.sent_at) || inThisWeek(i.scheduled_at))
    .sort((a, b) => {
      // Scheduled soonest first, then the rest by recency
      const at = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Infinity
      const bt = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Infinity
      if (at !== bt) return at - bt
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })

  const issueLink = (i: DashIssue) =>
    i.status === 'SENT'
      ? `/campaigns/${i.campaign_id ?? ''}/issues/${i.id}/stats`
      : `/campaigns/${i.campaign_id ?? ''}/issues/${i.id}/edit`

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Everything at a glance — subscribers, leads, campaigns and sends</p>
      </div>

      {/* Module stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        <StatCard label="Subscribed members" value={stats?.subscribed ?? '—'} icon={Users} color="bg-brand-600" to="/contacts" />
        <StatCard label="Companies (CH)" value={stats?.chCompanies?.toLocaleString() ?? '—'} icon={Building2} color="bg-indigo-600" to="/leads" />
        <StatCard label="Deliverable leads" value={stats?.deliverable ?? '—'} icon={Send} color="bg-emerald-600" to="/leads" />
        <StatCard label="Imported leads" value={stats?.imported ?? '—'} icon={Upload} color="bg-violet-600" to="/leads-import" />
        <StatCard label="Segments" value={stats?.segments ?? '—'} icon={Layers} color="bg-sky-600" to="/segments" />
        <StatCard label="Campaigns" value={stats?.campaigns ?? '—'} icon={Megaphone} color="bg-orange-500" to="/campaigns" />
        <StatCard label="Issues sent" value={stats?.issuesSent ?? '—'} icon={Mail} color="bg-green-600" to="/campaigns" />
        <StatCard label="Emails delivered" value={stats?.emailsSent?.toLocaleString() ?? '—'} icon={Send} color="bg-teal-600" to="/campaigns" />
        <StatCard label="Opens" value={stats?.opens?.toLocaleString() ?? '—'} icon={Eye} color="bg-pink-600" to="/campaigns" />
      </div>

      {/* Sequences — the daily at-a-glance view */}
      {!!campaignStats?.filter(c => c.kind === 'sequence').length && (
        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">🔁 Sequences</h2>
            <span className="text-[11px] text-gray-400">pending / received per step</span>
          </div>
          <div className="divide-y divide-gray-100">
            {campaignStats!.filter(c => c.kind === 'sequence').map(c => (
              <div key={c.id} className="px-5 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/campaigns/${c.id}`} className="flex items-center gap-2 group min-w-0">
                    <span className="w-6 h-6 rounded flex items-center justify-center text-sm shrink-0 select-none"
                      style={{ backgroundColor: (c.colour as string) + '20' }}>{c.icon as string}</span>
                    <span className="font-medium text-gray-900 group-hover:text-brand-600 truncate">{c.name as string}</span>
                  </Link>
                  <div className="ml-auto flex items-center gap-3 text-[11px] whitespace-nowrap">
                    <span className="text-gray-500">Total <strong className="text-gray-800">{c.enrolTotal}</strong></span>
                    <span className="text-gray-500">Week <strong className="text-emerald-700">+{c.enrolThisWeek}</strong></span>
                    <span className="text-gray-500">Month <strong className="text-emerald-700">+{c.enrolThisMonth}</strong></span>
                    <span className="text-gray-500">Active <strong className="text-green-700">{c.enrolActive}</strong></span>
                    {c.enrolHold > 0 && <span className="text-gray-500">Hold <strong className="text-amber-700">{c.enrolHold}</strong></span>}
                    <span className="text-gray-500">Done <strong className="text-gray-700">{c.enrolDone}</strong></span>
                    <span className="text-gray-500">Sent <strong className="text-gray-800">{c.emailsSent}</strong></span>
                  </div>
                </div>

                {/* Per-step funnel — pending is what still has to go out */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {c.steps.length === 0 ? (
                    <span className="text-[11px] text-amber-600">No steps configured yet</span>
                  ) : c.steps.map(st => (
                    <span key={st.id}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-gray-50 border-gray-200"
                      title={st.subject}>
                      <span className="font-semibold text-gray-700">#{st.no}</span>
                      <span className={st.pending > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}>{st.pending}</span>
                      <span className="text-gray-300">/</span>
                      <span className="text-gray-600">{st.done}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Broadcast campaigns */}
      {!!campaignStats?.filter(c => c.kind !== 'sequence').length && (
        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">📢 Broadcast campaigns</h2>
            <Link to="/campaigns" className="text-xs text-brand-600 hover:underline">Manage →</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-400">
                <tr className="text-left">
                  <th className="px-5 py-2">Campaign</th>
                  <th className="px-3 py-2 text-right">Members</th>
                  <th className="px-3 py-2 text-right">Active</th>
                  <th className="px-3 py-2 text-right">+ Week</th>
                  <th className="px-3 py-2 text-right">+ Month</th>
                  <th className="px-3 py-2 text-right">Issues</th>
                  <th className="px-5 py-2 text-right">Emails sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {campaignStats!.filter(c => c.kind !== 'sequence').map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-5 py-2">
                      <Link to={`/campaigns/${c.id}`} className="flex items-center gap-2 group">
                        <span className="w-6 h-6 rounded flex items-center justify-center text-sm shrink-0 select-none"
                          style={{ backgroundColor: (c.colour as string) + '20' }}>{c.icon as string}</span>
                        <span className="font-medium text-gray-800 group-hover:text-brand-600">{c.name as string}</span>
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-700">{c.members}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{c.activeMembers}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{c.membersThisWeek ? `+${c.membersThisWeek}` : '—'}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{c.membersThisMonth ? `+${c.membersThisMonth}` : '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-500">{c.issuesSent}/{c.issues}</td>
                    <td className="px-5 py-2 text-right font-semibold text-gray-700">{c.emailsSent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* This week + ongoing issues */}
      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">This week &amp; ongoing issues</h2>
          <Link to="/campaigns" className="text-sm text-brand-600 hover:underline">All campaigns</Link>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : !shown.length ? (
          <div className="py-10 text-center text-sm text-gray-400">
            Nothing in flight. <Link to="/campaigns" className="text-brand-600 hover:underline">Open a campaign to create an issue →</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-medium text-gray-500 border-b border-gray-100">
                <th className="px-5 py-3 text-left">Subject</th>
                <th className="px-5 py-3 text-left">Campaign</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {shown.map(issue => (
                <tr key={issue.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-gray-900">
                    <Link to={issueLink(issue)} className="hover:text-brand-600">{issue.subject}</Link>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">{issue.campaigns?.name ?? '—'}</td>
                  <td className="px-5 py-3"><StatusBadge status={issue.status} /></td>
                  <td className="px-5 py-3 text-gray-500 text-xs">
                    {issue.status === 'SCHEDULED' && issue.scheduled_at ? (
                      <span className="flex items-center gap-1 text-indigo-600"><Clock className="w-3 h-3" />{formatDateTime(issue.scheduled_at)}</span>
                    ) : issue.sent_at ? (
                      <>sent {formatDateTime(issue.sent_at)}</>
                    ) : (
                      <>created {formatDate(issue.created_at)}</>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
